import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { UnassignReasonsStore } from '@/stores/UnassignReasonsStore';
import { mapValueProperty } from '@/utils/mapValueProperty';
import { orderByField } from '@/utils/orderBy';
import type { IUnassignReasonInfo } from '~/api/IUnassignReasonInfo';
import type { IUnassignReasonRespMsg } from '~/api/messages/IUnassignReasonRespMsg';
import type { IUnassignReasonUpdateRespMsg } from '~/api/messages/IUnassignReasonUpdateRespMsg';

@SignalrListener
@service(UnassignReasonsManager)
export class UnassignReasonsManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(UnassignReasonsStore) protected unassignReasonsStore: UnassignReasonsStore;

  find(id: string) {
    return this.unassignReasonsStore.find(id);
  }

  get all() {
    const reasons = this.unassignReasonsStore.all()
      .filter((reason) => reason.status !== STATUS.DISABLED);
    return orderByField(reasons, this.unassignReasonsStore.valueKey);
  }

  reset() {
    this.unassignReasonsStore.set([]);
  }

  updateUnassignReasons(unassignReasons: IUnassignReasonInfo[]) {
    this.unassignReasonsStore.batchUpdate(mapValueProperty(unassignReasons));
  }

  @SignalrMessage('UnassignReasonRespMsg')
  protected onUnassignReasonRespMsg(response: IUnassignReasonRespMsg) {
    this.updateUnassignReasons(response.unassignReasons);
  }

  @SignalrMessage('UnassignReasonUpdateRespMsg')
  protected onUnassignReasonUpdateRespMsg(response: IUnassignReasonUpdateRespMsg) {
    this.updateUnassignReasons([response.unassignReason]);
  }
}
